import { useCallback, useRef } from 'react';

export function useTooltipVideoPlayer() {
  // Video control refs
  const videoRef = useRef<HTMLVideoElement>(null);
  const currentTimeRef = useRef<number>(0);
  const isTooltipOpenRef = useRef<boolean>(false);

  const playVideoFromCurrentTime = useCallback(() => {
    if (videoRef.current && isTooltipOpenRef.current) {
      videoRef.current.currentTime = currentTimeRef.current;
      videoRef.current.play().catch((error) => {
        // Ignore AbortError as it's expected when tooltip closes quickly
        // (pause called before play actually starts https://developer.chrome.com/blog/play-request-was-interrupted)
        if (error.name !== 'AbortError') {
          throw error;
        }
      });
    }
  }, []);

  const handleTooltipOpen = useCallback(() => {
    isTooltipOpenRef.current = true;
    // Use requestAnimationFrame to ensure the video element is rendered before playing
    // as the video is rendered after the tooltip is opened
    requestAnimationFrame(playVideoFromCurrentTime);
  }, [playVideoFromCurrentTime]);

  const handleTooltipClose = useCallback(() => {
    isTooltipOpenRef.current = false;
    if (videoRef.current) {
      currentTimeRef.current = videoRef.current.currentTime;
      videoRef.current.pause();
    }
  }, []);

  const handleVideoLoad = useCallback(() => {
    playVideoFromCurrentTime();
  }, [playVideoFromCurrentTime]);

  return {
    videoRef,
    handleTooltipOpen,
    handleTooltipClose,
    handleVideoLoad,
  };
}
